import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import FileType from 'file-type/browser'
import toast from '../components/toast'

export const downloadTemplateFile = url => {
	const link = document.createElement('a')
	link.href = url
	link.click()
	toast.success({
		title: 'Successful.',
		body: 'Template file downloaded',
	})
}

// arrayImages = [{ fileName, path }]

// Normalize legacy S3 path-style URLs (s3.amazonaws.com/<bucket>/<key>) to virtual-hosted
// style (<bucket>.s3.amazonaws.com/<key>). Path-style URLs do not receive CORS headers
// from the bucket so fetch() fails silently; virtual-hosted does.
const normalizeS3Url = url => {
	if (!url) return url
	return url.replace(
		/https?:\/\/s3\.amazonaws\.com\/([^/]+)\/+/,
		'https://$1.s3.amazonaws.com/',
	)
}

// Zips an array of files and saves to disk. Skips any files that fail to fetch.
export const downloadZipFile = (zipName, arrayImages = []) => {
	if (!arrayImages || arrayImages.length <= 0) return Promise.resolve()
	const zip = new JSZip()
	const tasks = []

	// Helper: extract extension from URL path
	const extFromUrl = url => {
		try {
			const pathname = new URL(url, 'https://x').pathname
			const dot = pathname.lastIndexOf('.')
			if (dot !== -1) return pathname.substring(dot + 1).toLowerCase()
		} catch (e) {
			/* ignore */
		}
		return null
	}

	for (let i = 0; i < arrayImages.length; i++) {
		const { fileName, path, folder } = arrayImages[i]
		const fetchUrl = normalizeS3Url(path)
		tasks.push(
			new Promise(resolve => {
				return fetch(fetchUrl, { cache: 'no-store' })
					.then(response => {
						if (!response.ok) return resolve()
						return response.blob()
					})
					.then(async blob => {
						if (!blob) return resolve()
						const fileType = await FileType.fromBlob(blob)
						const ext = fileType ? fileType.ext : extFromUrl(path) || 'bin'
						const prefix = folder ? `${folder}/` : ''
						zip.file(`${prefix}${i + 1}.${fileName}.${ext}`, blob)
						resolve()
					})
					.catch(() => {
						resolve()
					})
			}),
		)
	}

	return Promise.all(tasks).then(() => {
		if (Object.keys(zip.files).length === 0) return
		return zip.generateAsync({ type: 'blob' }).then(content => {
			return saveAs(content, `${zipName}.zip`)
		})
	})
}

// Triggers a browser download for a single file. For cross-origin S3 URLs the
// `download` anchor attribute is ignored by browsers (file opens in-tab),
// so we first try fetch → blob → saveAs (creates a same-origin blob URL that
// honors download). Falls back to anchor-click if fetch fails (e.g. no CORS).
export const downloadImage = (path, fileName = '', callback) => {
	if (!path) {
		if (callback) callback()
		return Promise.resolve()
	}

	const anchorClick = (href, name) => {
		const link = document.createElement('a')
		link.href = href
		if (name) link.download = name
		link.rel = 'noopener'
		document.body.appendChild(link)
		link.click()
		link.remove()
	}

	const extFromUrl = url => {
		try {
			const pathname = new URL(url, 'https://x').pathname
			const dot = pathname.lastIndexOf('.')
			if (dot !== -1) return pathname.substring(dot + 1).toLowerCase()
		} catch (e) {
			/* ignore */
		}
		return null
	}

	const fetchUrl = normalizeS3Url(path)
	return fetch(fetchUrl, { cache: 'no-store' })
		.then(response => {
			if (!response.ok) throw new Error(`status ${response.status}`)
			return response.blob()
		})
		.then(blob => {
			let safeName = fileName || 'download'
			if (!/\.[a-z0-9]{1,6}$/i.test(safeName)) {
				const ext = extFromUrl(path)
				if (ext) safeName = `${safeName}.${ext}`
			}
			return saveAs(blob, safeName)
		})
		.catch(() => {
			anchorClick(path, fileName)
		})
		.finally(() => {
			if (callback) callback()
		})
}
